import { useCallback, useMemo, useState, type ReactNode } from 'react'
import {
  ConnectionProvider,
  WalletProvider,
} from '@solana/wallet-adapter-react'
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui'
import type { Adapter, WalletError } from '@solana/wallet-adapter-base'
import { RPC_ENDPOINT } from '../lib/constants'

import '@solana/wallet-adapter-react-ui/styles.css'

/**
 * Wallet Standard ONLY.
 *
 * WalletProvider → useStandardWalletAdapters([]) auto-registers installed
 * Standard wallets (Phantom, Solflare, MetaMask Solana, Brave, OKX, Exodus, …).
 *
 * Do NOT also pass PhantomWalletAdapter / SolflareWalletAdapter — that creates
 * duplicates (e.g. MetaMask twice) and can make select/connect no-op.
 *
 * autoConnect=true so modal select → adapter.connect() (extension popup).
 * Page-load reconnect only happens for a previously chosen wallet name.
 */
export function SolanaWalletProvider({ children }: { children: ReactNode }) {
  const endpoint = RPC_ENDPOINT
  const wallets = useMemo(() => [], [])
  const [error, setError] = useState<string | null>(null)

  const onWalletError = useCallback((err: WalletError, adapter?: Adapter) => {
    const msg = err?.message ?? String(err)
    console.error('[wallet]', msg, adapter?.name ?? '')
    setError(msg)
  }, [])

  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider wallets={wallets} autoConnect onError={onWalletError}>
        <WalletModalProvider>
          {error ? (
            <div className="wallet-error-toast" role="alert">
              <strong>Wallet:</strong> <span>{error}</span>
              <button
                type="button"
                className="wallet-error-toast-dismiss"
                aria-label="Dismiss"
                onClick={() => setError(null)}
              >
                ×
              </button>
            </div>
          ) : null}
          {children}
        </WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  )
}
